import React from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";

/**
 * Resolve a responsive prop:
 *  - number
 *  - { xs, sm, md, lg, xl }
 */
function useResponsiveValue(value, fallback = 0) {
    const theme = useTheme();
    const isXl = useMediaQuery(theme.breakpoints.up("xl"));
    const isLg = useMediaQuery(theme.breakpoints.only("lg"));
    const isMd = useMediaQuery(theme.breakpoints.only("md"));
    const isSm = useMediaQuery(theme.breakpoints.only("sm"));

    if (typeof value === "number") return value;

    if (value && typeof value === "object") {
        if (isXl && value.xl != null) return value.xl;
        if (isLg && value.lg != null) return value.lg;
        if (isMd && value.md != null) return value.md;
        if (isSm && value.sm != null) return value.sm;
        return value.xs ?? fallback;
    }

    return fallback;
}

/** Deterministic PRNG */
function mulberry32(seed) {
    let a = seed >>> 0;
    return function () {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

const lerp = (a, b, t) => a + (b - a) * t;

/**
 * Rotated rectangle's axis-aligned bounding box (AABB).
 * Input: width/height, rotation in degrees.
 * Output: { w, h } of the AABB.
 */
function rotatedAABB(w, h, deg) {
    const rad = (deg * Math.PI) / 180;
    const c = Math.abs(Math.cos(rad));
    const s = Math.abs(Math.sin(rad));
    return {
        w: w * c + h * s,
        h: w * s + h * c,
    };
}

/**
 * Loads an image and returns its aspect ratio (h / w).
 */
function useImageAspect(src, fallback = 1) {
    const [ratio, setRatio] = React.useState(fallback);

    React.useEffect(() => {
        if (!src) return;
        let alive = true;

        const img = new Image();
        img.onload = () => {
            if (!alive) return;
            const w = img.naturalWidth || 1;
            const h = img.naturalHeight || 1;
            setRatio(h / w);
        };
        img.onerror = () => {
            if (!alive) return;
            setRatio(fallback);
        };
        img.src = src;

        return () => {
            alive = false;
        };
    }, [src, fallback]);

    return ratio;
}

/**
 * Find max width w such that rotated AABB(w, w*aspect) fits within availW/availH.
 */
function maxWidthThatFits({ aspect, rot, availW, availH }) {
    const aabbAt1 = rotatedAABB(1, aspect, rot);
    const wMaxByW = aabbAt1.w > 0 ? availW / aabbAt1.w : 0;
    const wMaxByH = aabbAt1.h > 0 ? availH / aabbAt1.h : 0;
    return Math.max(0, Math.min(wMaxByW, wMaxByH));
}

/**
 * DecorativeSidePattern
 * - Fully packed (no overlap by construction)
 * - Never "skips" due to size: it shrinks to fit the cell/subcell
 */
export default function DecorativeSidePattern({
                                                  design1,
                                                  design2,

                                                  opacity = 0.12,
                                                  zIndex = 0,
                                                  blur = 0,
                                                  seed = 1234,

                                                  density = { xs: 1.0, sm: 1.0, md: 1.0, lg: 1.0, xl: 1.0 }, // items per cell (can be > 1)
                                                  cellSize = { xs: 250, sm: 300, md: 360, lg: 420, xl: 480 },

                                                  sizeRange = {
                                                      xs: [200, 300],
                                                      sm: [100, 200],
                                                      md: [150, 250],
                                                      lg: [200, 350],
                                                      xl: [250, 400],
                                                  },

                                                  rotateRange = [-45, 45],
                                                  allowFlip = false,

                                                  collisionPadding = 0,
                                                  padding = { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },

                                                  // safety: avoid 0px images if density is insane
                                                  minRenderedWidth = 8,
                                              }) {
    const containerRef = React.useRef(null);
    const [bounds, setBounds] = React.useState({ width: 0, height: 0 });

    const resolvedDensity = useResponsiveValue(density, 1);
    const resolvedCellSize = useResponsiveValue(cellSize, 320);
    const resolvedPadding = useResponsiveValue(padding, 0);
    const resolvedSizeRange = useResponsiveValue(sizeRange, [160, 320]);

    const aspect1 = useImageAspect(design1, 1);
    const aspect2 = useImageAspect(design2, 1);

    React.useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const update = () => {
            const width = el.clientWidth || 0;
            const height = Math.max(el.scrollHeight || 0, el.clientHeight || 0);
            setBounds({ width, height });
        };

        update();

        const ro = new ResizeObserver(update);
        ro.observe(el);

        window.addEventListener("resize", update);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", update);
        };
    }, []);

    const items = React.useMemo(() => {
        const { width, height } = bounds;
        if (!width || !height || !design1 || !design2) return [];

        const rng = mulberry32(seed);

        const pad = resolvedPadding;
        const usableW = Math.max(0, width - pad * 2);
        const usableH = Math.max(0, height - pad * 2);

        const cs = Math.max(1, resolvedCellSize);
        const cols = Math.max(1, Math.ceil(usableW / cs));
        const rows = Math.max(1, Math.ceil(usableH / cs));

        const [minWRaw, maxWRaw] = Array.isArray(resolvedSizeRange)
            ? resolvedSizeRange
            : [160, 320];

        const preferredMinW = Math.max(1, minWRaw);
        const preferredMaxW = Math.max(preferredMinW, maxWRaw);

        const basePerCell = Math.max(0, Math.floor(resolvedDensity));
        const extraProb = Math.max(0, resolvedDensity - basePerCell);

        const out = [];

        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const cellX = pad + c * cs;
                const cellY = pad + r * cs;

                const countInCell = basePerCell + (rng() < extraProb ? 1 : 0);
                if (countInCell <= 0) continue;

                const subCols = Math.ceil(Math.sqrt(countInCell));
                const subRows = Math.ceil(countInCell / subCols);
                const subW = cs / subCols;
                const subH = cs / subRows;

                for (let i = 0; i < countInCell; i++) {
                    const sc = i % subCols;
                    const sr = Math.floor(i / subCols);

                    const subX = cellX + sc * subW;
                    const subY = cellY + sr * subH;

                    const useFirst = rng() < 0.5;
                    const src = useFirst ? design1 : design2;
                    const aspect = useFirst ? aspect1 : aspect2;

                    const rot = lerp(rotateRange[0], rotateRange[1], rng());

                    const availW = Math.max(0, subW - collisionPadding * 2);
                    const availH = Math.max(0, subH - collisionPadding * 2);

                    const wFitMax = maxWidthThatFits({ aspect, rot, availW, availH });
                    if (wFitMax < minRenderedWidth) continue;

                    // ✅ Key change:
                    // Use your sizeRange as "preference", but clamp down to fit.
                    const wMax = Math.min(preferredMaxW, wFitMax);
                    const wMin = Math.min(preferredMinW, wMax); // shrink min if needed

                    const w = lerp(wMin, wMax, rng());
                    const h = w * aspect;

                    const aabb = rotatedAABB(w, h, rot);

                    // Place AABB inside subcell with deterministic jitter
                    const innerX = subX + collisionPadding;
                    const innerY = subY + collisionPadding;

                    const slackX = Math.max(0, availW - aabb.w);
                    const slackY = Math.max(0, availH - aabb.h);

                    const ax = innerX + (slackX > 0 ? rng() * slackX : 0);
                    const ay = innerY + (slackY > 0 ? rng() * slackY : 0);

                    // Center actual image inside its AABB
                    const left = ax + (aabb.w - w) / 2;
                    const top = ay + (aabb.h - h) / 2;

                    const flipX = allowFlip && rng() < 0.35 ? -1 : 1;
                    const flipY = allowFlip && rng() < 0.18 ? -1 : 1;

                    out.push({
                        key: `scatter-${r}-${c}-${i}`,
                        src,
                        left,
                        top,
                        w,
                        h,
                        rot,
                        flipX,
                        flipY,
                    });
                }
            }
        }

        return out;
    }, [
        bounds,
        design1,
        design2,
        seed,
        resolvedDensity,
        resolvedCellSize,
        resolvedPadding,
        resolvedSizeRange,
        aspect1,
        aspect2,
        rotateRange,
        allowFlip,
        collisionPadding,
        minRenderedWidth,
    ]);

    return (
        <Box
            ref={containerRef}
            aria-hidden="true"
            sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                overflow: "hidden",
                zIndex,
            }}
        >
            {items.map((it) => (
                <Box
                    key={it.key}
                    component="img"
                    src={it.src}
                    sx={{
                        position: "absolute",
                        left: it.left,
                        top: it.top,
                        width: it.w,
                        height: it.h,
                        opacity,
                        filter: blur ? `blur(${blur}px)` : "none",
                        transform: `rotate(${it.rot}deg) scale(${it.flipX}, ${it.flipY})`,
                        transformOrigin: "center",
                        userSelect: "none",
                    }}
                />
            ))}
        </Box>
    );
}
